/**
 * Merge a selection of cadastre parcels into a single composite "parcel"
 * used by the fiche persistence layer.
 *
 * Why this exists: a real-estate property can span multiple cadastral
 * parcels (e.g. a house + a detached garage on its own parcel + a strip
 * of garden on a third). The Nouvelle picker lets the user shift-click
 * to select several, and downstream code expects a single `CadastreParcel`
 * shape. We fake it.
 *
 * Caveats:
 *   - `geometry` is a pragmatic stitch (MultiPolygon of each selected
 *     polygon's outer + holes) rather than a true topological union.
 *     That's fine for display, point-in-polygon tests, and BD TOPO
 *     building intersection — each sub-polygon is still valid. A proper
 *     union would need Turf.js in the frontend and for two adjacent
 *     parcels the sub-polygons touch cleanly at the shared boundary
 *     anyway, so the visual difference is nil.
 *   - `surface_m2` is the sum of the components. It matches the user's
 *     mental model (total property surface).
 *   - `section` / `numero` / `prefixe` get a composite label like
 *     "AB-0183+AB-0184" so the fiche can render something human. The
 *     `id` uses a "MULTI:" prefix so downstream code that still cares
 *     about the per-parcel cadastre reference (DVF `idpar` lookups,
 *     typically) can detect and branch — either fall back to one
 *     component or query each separately.
 *   - `codeCommune` / `codeDepartement` are taken from the first
 *     parcel. We assume the user doesn't shift-click across communes
 *     (cadastre buffer is 30 m, commune boundaries are much further
 *     apart in practice).
 */

import type { CadastreParcel } from "./types";

export function mergeParcels(parcels: CadastreParcel[]): CadastreParcel {
  if (parcels.length === 0) {
    throw new Error("mergeParcels: empty input");
  }
  if (parcels.length === 1) return parcels[0];

  // Sort by section+numero for a stable composite id regardless of the
  // order the user clicked.
  const sorted = [...parcels].sort((a, b) => a.id.localeCompare(b.id));

  const geometry = stitchMultiPolygon(sorted.map((p) => p.geometry));
  const surface_m2 = sorted.reduce(
    (sum, p) => sum + (p.surface_m2 || 0),
    0,
  );

  return {
    id: `MULTI:${sorted.map((p) => p.id).join("+")}`,
    section: sorted.map((p) => p.section).join("+"),
    numero: sorted.map((p) => p.numero).join("+"),
    prefixe: sorted[0].prefixe,
    codeCommune: sorted[0].codeCommune,
    codeDepartement: sorted[0].codeDepartement,
    surface_m2,
    geometry,
  };
}

/**
 * Return every cadastre ID the parcel is "composed of".
 *
 * For a regular parcel this is a singleton `[parcel.id]`. For a
 * `MULTI:` composite produced by `mergeParcels`, it's the sorted list
 * of component IDs extracted from the composite id. Used wherever we
 * need to answer "is this candidate part of the user's selection?" —
 * both in the inline picker list (highlight) and the map overlay
 * (neighbor filter, subject highlighting).
 */
export function parcelComponentIds(parcelId: string): string[] {
  if (parcelId.startsWith("MULTI:")) {
    return parcelId.slice("MULTI:".length).split("+").filter(Boolean);
  }
  return [parcelId];
}

/**
 * Human-readable label for a (possibly composite) parcel. Used in the
 * picker confirmation banner and the fiche header.
 */
export function parcelLabel(p: CadastreParcel): string {
  if (p.id.startsWith("MULTI:")) {
    const sections = p.section.split("+");
    const numeros = p.numero.split("+");
    const refs = sections.map((s, i) => `${s}-${numeros[i] ?? ""}`);
    return `${refs.join(" · ")} (${refs.length} parcelles)`;
  }
  return `${p.section}-${p.numero}`;
}

/**
 * Flatten an array of Polygon / MultiPolygon geometries into a single
 * MultiPolygon. Components that aren't valid Polygons are skipped.
 */
function stitchMultiPolygon(geoms: unknown[]): unknown {
  const polys: number[][][][] = [];
  for (const g of geoms) {
    if (!g || typeof g !== "object") continue;
    const geom = g as { type?: string; coordinates?: unknown };
    if (geom.type === "Polygon") {
      polys.push(geom.coordinates as number[][][]);
    } else if (geom.type === "MultiPolygon") {
      for (const p of geom.coordinates as number[][][][]) {
        polys.push(p);
      }
    }
  }
  return {
    type: "MultiPolygon",
    coordinates: polys,
  };
}
